import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, FileTextIcon, PlusIcon, AlertTriangleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { DashboardScreen } from './DashboardScreen';
interface PTPListItem {
  id: string;
  task: string;
  location: string;
  contractor: string;
  date: string;
  status: 'complete' | 'in-progress' | 'draft';
  score?: number;
  crewSize: number;
}
const PTP_LIST: PTPListItem[] = [{
  id: 'PTP-2026-00847',
  task: 'Steel Beam Installation Using Mobile Crane',
  location: 'Level 2 Structural Frame',
  contractor: 'Apex Steel Works',
  date: 'June 12, 2026',
  status: 'complete',
  score: 72,
  crewSize: 5
}, {
  id: 'PTP-2026-00846',
  task: 'Concrete Pour — East Wing Foundation',
  location: 'Ground Level East',
  contractor: 'Summit Concrete Ltd.',
  date: 'June 11, 2026',
  status: 'complete',
  score: 91,
  crewSize: 8
}, {
  id: 'PTP-2026-00845',
  task: 'Scaffolding Erection — North Facade',
  location: 'North Exterior',
  contractor: 'Vertex Scaffolding',
  date: 'June 10, 2026',
  status: 'complete',
  score: 85,
  crewSize: 4
}, {
  id: 'PTP-2026-00844',
  task: 'Electrical Conduit Routing — Level 1',
  location: 'Level 1 Interior',
  contractor: 'BrightLine Electrical',
  date: 'June 9, 2026',
  status: 'complete',
  score: 94,
  crewSize: 3
}];
const statusConfig = {
  complete: {
    label: 'Complete',
    icon: CheckCircle2Icon,
    iconColor: 'text-green-500',
    bg: 'bg-green-500/10',
    text: 'text-green-400'
  },
  'in-progress': {
    label: 'In Progress',
    icon: ClockIcon,
    iconColor: 'text-amber-500',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400'
  },
  draft: {
    label: 'Draft',
    icon: FileTextIcon,
    iconColor: 'text-slate-500',
    bg: 'bg-slate-500/10',
    text: 'text-slate-400'
  }
};
export function Screen21_PTPList({
  data,
  onNext,
  showNewPTP = false
}: ScreenProps & {
  showNewPTP?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<'plans' | 'dashboard'>('plans');
  const visiblePTPs = showNewPTP ? PTP_LIST : PTP_LIST.slice(1);
  return <div className="flex flex-col h-full" data-id="element-971">
      <div className="mb-4" data-id="element-972">
        <h2 className="text-2xl font-bold text-white mb-1" data-id="element-973">Pre-Task Plans</h2>
        <p className="text-slate-400 text-sm" data-id="element-974">Downtown Office Tower</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-slate-900 p-1 rounded-lg flex mb-5 flex-shrink-0" data-id="element-975">
        <button onClick={() => setActiveTab('plans')} className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'plans' ? 'bg-orange-500 text-white shadow-sm' : 'text-slate-400'}`} data-id="element-976">
          Plans
        </button>
        <button onClick={() => setActiveTab('dashboard')} className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'dashboard' ? 'bg-orange-500 text-white shadow-sm' : 'text-slate-400'}`} data-id="element-977">
          Dashboard
        </button>
      </div>

      {activeTab === 'dashboard' ? <DashboardScreen data-id="element-978" /> : <>
          {/* Summary bar */}
          <div className="flex gap-2 mb-5" data-id="element-979">
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-center" data-id="element-980">
              <p className="text-xl font-bold text-white" data-id="element-981">
                {visiblePTPs.length}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-982">
                Total PTPs
              </p>
            </div>
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-center" data-id="element-983">
              <p className="text-xl font-bold text-green-400" data-id="element-984">
                {visiblePTPs.filter(p => p.status === 'complete').length}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-985">
                Complete
              </p>
            </div>
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-center" data-id="element-986">
              <p className="text-xl font-bold text-white" data-id="element-987">
                {Math.round(visiblePTPs.reduce((sum, p) => sum + (p.score || 0), 0) / visiblePTPs.length)}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-988">
                Avg Score
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-2.5" data-id="element-989">
            {visiblePTPs.map((ptp, i) => {
          const config = statusConfig[ptp.status];
          const StatusIcon = config.icon;
          const isFirst = i === 0;
          return <motion.div key={ptp.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.08,
            duration: 0.3
          }} className={`bg-slate-900 border rounded-xl p-4 ${isFirst ? 'border-orange-500/30 ring-1 ring-orange-500/10' : 'border-slate-800'}`} data-id="element-990">
                  <div className="flex items-start justify-between gap-3 mb-2" data-id="element-991">
                    <div className="flex-1 min-w-0" data-id="element-992">
                      <p className="text-white font-semibold text-sm leading-snug" data-id="element-993">
                        {ptp.task}
                      </p>
                      <p className="text-slate-500 text-xs mt-0.5" data-id="element-994">
                        {ptp.location}
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5" data-id="element-995">
                        {ptp.contractor}
                      </p>
                    </div>
                    <div className={`${config.bg} px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0`} data-id="element-996">
                      <StatusIcon className={`w-3 h-3 ${config.iconColor}`} data-id="element-997" />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text}`} data-id="element-998">
                        {config.label}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs" data-id="element-999">
                    <span className="text-slate-500" data-id="element-1000">{ptp.date}</span>
                    <span className="text-slate-700" data-id="element-1001">·</span>
                    <span className="text-slate-500" data-id="element-1002">
                      {ptp.crewSize} workers
                    </span>
                    <span className="text-slate-700" data-id="element-1003">·</span>
                    {ptp.score && <span className="flex items-center gap-1" data-id="element-1004">
                        {ptp.score < 80 && <AlertTriangleIcon className="w-3 h-3 text-amber-500" data-id="element-1005" />}
                        <span className={ptp.score >= 80 ? 'text-green-400 font-medium' : 'text-amber-400 font-medium'} data-id="element-1006">
                          {ptp.score}/100
                        </span>
                      </span>}
                  </div>

                  {isFirst && <div className="mt-2 pt-2 border-t border-slate-800" data-id="element-1007">
                      <span className="text-[10px] text-orange-400 font-bold uppercase tracking-wider" data-id="element-1008">
                        Just completed
                      </span>
                    </div>}

                  <div className="text-[10px] text-slate-600 mt-1" data-id="element-1009">
                    {ptp.id}
                  </div>
                </motion.div>;
        })}
          </div>

          <div className="pt-4 bg-slate-950" data-id="element-1010">
            <PrimaryButton onClick={onNext} icon={<PlusIcon className="w-5 h-5" data-id="element-1012" />} data-id="element-1011">
              New Pre-Task Plan
            </PrimaryButton>
          </div>
        </>}
    </div>;
}